"use client"

import { createContext, useCallback, useContext, useState } from "react"

import type {
  Gender,
  SignupContextType,
  SignupState,
  SignupStep,
} from "./types"

const INITIAL_STATE: SignupState = {
  step: "email",
  email: "",
  firstName: "",
  lastName: "",
  birthDate: "",
  gender: "",
  isLoading: false,
  isGoogleLoading: false,
  isMagicLinkSent: false,
  error: null,
}

const SignupContext = createContext<SignupContextType | null>(null)

interface SignupProviderProps {
  children: React.ReactNode
}

export function SignupProvider({ children }: SignupProviderProps) {
  const [state, setState] = useState<SignupState>(INITIAL_STATE)

  const setStep = useCallback((step: SignupStep) => {
    setState((prev) => ({ ...prev, step }))
  }, [])

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({ ...prev, email }))
  }, [])

  const setFirstName = useCallback((firstName: string) => {
    setState((prev) => ({ ...prev, firstName }))
  }, [])

  const setLastName = useCallback((lastName: string) => {
    setState((prev) => ({ ...prev, lastName }))
  }, [])

  const setBirthDate = useCallback((birthDate: string) => {
    setState((prev) => ({ ...prev, birthDate }))
  }, [])

  const setGender = useCallback((gender: Gender | "") => {
    setState((prev) => ({ ...prev, gender }))
  }, [])

  const setIsLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }))
  }, [])

  const setIsGoogleLoading = useCallback((isGoogleLoading: boolean) => {
    setState((prev) => ({ ...prev, isGoogleLoading }))
  }, [])

  const setIsMagicLinkSent = useCallback((isMagicLinkSent: boolean) => {
    setState((prev) => ({ ...prev, isMagicLinkSent }))
  }, [])

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }))
  }, [])

  const reset = useCallback(() => {
    setState(INITIAL_STATE)
  }, [])

  return (
    <SignupContext.Provider
      value={{
        ...state,
        setStep,
        setEmail,
        setFirstName,
        setLastName,
        setBirthDate,
        setGender,
        setIsLoading,
        setIsGoogleLoading,
        setIsMagicLinkSent,
        setError,
        reset,
      }}
    >
      {children}
    </SignupContext.Provider>
  )
}

export function useSignupContext() {
  const context = useContext(SignupContext)
  if (!context) {
    throw new Error("useSignupContext must be used within SignupProvider")
  }
  return context
}
